import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AccountLayout } from "@/components/account/AccountLayout";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/store";
import { API_BASE_URL } from "@/lib/api";
import { 
  Package, 
  Heart, 
  MapPin, 
  User, 
  ArrowRight, 
  Truck, 
  Sparkles, 
  ChevronRight, 
  ShoppingBag,
  Pencil,
  CreditCard,
  ClipboardList,
  Shield,
  ShieldAlert,
  LogOut,
  SlidersHorizontal
} from "lucide-react";
import { mockAddresses } from "@/lib/mock-account";

export const Route = createFileRoute("/account/")({
  head: () => ({
    meta: [
      { title: "Account Overview — Royal Lumina" },
      { name: "description", content: "Overview of your Royal Lumina orders, saved candles, addresses and member status." },
    ],
  }),
  component: AccountDashboardPage,
});

function AccountDashboardPage() {
  const { user, role, signOut } = useAuth();
  const { ids: wishlistIds } = useWishlist();

  const token = typeof window !== "undefined" ? localStorage.getItem("supabase.auth.token") || "" : "";

  // Fetch orders — only when user is confirmed authenticated (not stale token)
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ["my-orders", user?.id],
    enabled: !!user && !!token,
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/orders/my-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) return await res.json();
        console.error("[AccountDashboard] Orders fetch failed:", res.status);
      } catch (err: any) {
        console.error("[AccountDashboard] Orders fetch error:", err?.message ?? err);
      }
      return [];
    }
  });

  // Get addresses from localStorage or mock
  const addresses = typeof window !== "undefined"
    ? JSON.parse(localStorage.getItem("rl_addresses_v1") || JSON.stringify(mockAddresses))
    : mockAddresses;

  const defaultAddress = addresses.find((a: any) => a.default) || addresses[0];

  const rawName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split("@")[0] || "Valued Member";
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);
  const initials = name.charAt(0).toUpperCase();

  const isUserAdmin = role === "admin";
  const memberTier = isUserAdmin ? "Atelier Master Admin" : "Royal Circle VIP";
  const locationText = defaultAddress?.city ? `${defaultAddress.city}, ${defaultAddress.state}` : "Royal Lumina Atelier";

  const activeOrdersCount = orders.filter((o: any) => ["pending", "processing", "shipped"].includes(o.status)).length;
  const recentOrder = orders.length > 0 ? orders[0] : null;

  return (
    <AccountLayout title="Overview" subtitle="Welcome to your personal Royal Lumina sanctuary.">
      {/* MOBILE SPECIFIC PROFILE UI (MATCHING REFERENCE IMAGE) */}
      <div className="lg:hidden space-y-6">
        
        {/* Top Profile Header Bar */}
        <div className="flex items-center justify-between pt-1 pb-1">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
          </div>
          <h2 className="font-display text-xl font-bold text-primary">Profile</h2>
          <Link to="/account/profile" aria-label="Edit Profile" className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-primary hover:bg-gold/20 transition-colors">
            <Pencil className="h-4.5 w-4.5" />
          </Link>
        </div>

        {/* Hero Avatar & Stat Card Overlay */}
        <div className="relative pt-2 pb-2 text-center flex flex-col items-center">
          {/* Centered Large Circular Avatar */}
          <div className="relative z-20 h-24 w-24 rounded-full bg-gradient-to-br from-gold via-gold/80 to-gold/40 text-primary font-display text-4xl font-bold grid place-items-center shadow-xl border-4 border-background">
            {initials}
          </div>

          {/* Dual Stat Pill Overlay */}
          <div className="relative z-10 -mt-10 w-full max-w-xs bg-secondary/80 backdrop-blur-md rounded-[20px] pt-12 pb-4 px-6 border border-border/80 shadow-md flex items-center justify-around">
            <Link to="/account/wishlist" className="flex flex-col items-center group">
              <span className="font-display text-2xl font-bold text-primary group-hover:text-gold transition-colors">{wishlistIds.length}</span>
              <span className="text-[0.7rem] text-muted-foreground font-medium mt-0.5">Favorites</span>
            </Link>
            <div className="h-8 w-px bg-border/80" />
            <Link to="/account/orders" className="flex flex-col items-center group">
              <span className="font-display text-2xl font-bold text-primary group-hover:text-gold transition-colors">{orders.length}</span>
              <span className="text-[0.7rem] text-muted-foreground font-medium mt-0.5">Orders</span>
            </Link>
          </div>

          {/* User Name & Location Subtitle */}
          <div className="mt-4">
            <h3 className="font-display text-2xl font-bold text-primary tracking-tight">{name}</h3>
            <div className="flex items-center justify-center gap-1 mt-1 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 text-gold shrink-0" />
              <span>{locationText}</span>
            </div>
          </div>
        </div>

        {/* Structured Menu List Group (Matching Reference Image) */}
        <div className="bg-card rounded-[24px] border border-border/80 p-2 shadow-luxe space-y-1">
          <Link to="/account/addresses" className="flex items-center justify-between p-4 rounded-[16px] hover:bg-secondary/70 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-primary shrink-0">
                <MapPin className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="font-semibold text-sm text-primary">Shipping Address</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          <Link to="/account/profile" className="flex items-center justify-between p-4 rounded-[16px] hover:bg-secondary/70 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-primary shrink-0">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="font-semibold text-sm text-primary">Payment & Profile Settings</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          <Link to="/account/orders" className="flex items-center justify-between p-4 rounded-[16px] hover:bg-secondary/70 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-primary shrink-0">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="font-semibold text-sm text-primary">Order History</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          {isUserAdmin && (
            <Link to="/admin" className="flex items-center justify-between p-4 rounded-[16px] hover:bg-gold/10 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-gold/10 grid place-items-center text-gold shrink-0">
                  <Shield className="h-5 w-5 text-gold" />
                </div>
                <span className="font-semibold text-sm text-gold">Admin Atelier Portal</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gold" />
            </Link>
          )}

          <Link to="/contact" className="flex items-center justify-between p-4 rounded-[16px] hover:bg-secondary/70 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-primary shrink-0">
                <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="font-semibold text-sm text-primary">Privacy Policy & Help</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>

          <button onClick={signOut} className="w-full flex items-center justify-between p-4 rounded-[16px] hover:bg-destructive/10 transition-colors text-left">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-destructive/10 grid place-items-center text-destructive shrink-0">
                <LogOut className="h-5 w-5 text-destructive" />
              </div>
              <span className="font-semibold text-sm text-destructive">Logout</span>
            </div>
            <ChevronRight className="h-4 w-4 text-destructive/60" />
          </button>
        </div>
      </div>

      {/* DESKTOP & FULL DASHBOARD VIEW */}
      <div className="hidden lg:block space-y-8">
        
        {/* User Welcome Banner */}
        <div className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-cream rounded-[24px] p-6 sm:p-8 shadow-luxe border border-gold/20 relative overflow-hidden">
          <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-64 h-64 bg-[radial-gradient(circle_at_50%_50%,var(--gold)_0%,transparent_70%)] opacity-20 pointer-events-none" />
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gradient-to-br from-gold via-gold/80 to-gold/40 grid place-items-center text-primary font-display text-2xl sm:text-3xl font-bold shadow-lg border-2 border-background/20 shrink-0">
                {initials}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase px-3 py-1 rounded-full bg-gold/20 text-gold border border-gold/30">
                    {memberTier}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2 text-cream">
                  {name}
                </h3>
                <p className="text-xs text-cream/70 mt-1 font-mono">{user?.email}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2 md:pt-0 border-t md:border-t-0 border-cream/10">
              <Link to="/account/profile" className="btn-outline-dark text-cream border-cream/30 hover:bg-cream/10 h-10 px-5 text-xs uppercase tracking-wider font-semibold">
                <User className="h-4 w-4" /> Edit Profile
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {/* Stat 1: Total Orders */}
          <Link to="/account/orders" className="bg-card border border-border/80 rounded-[20px] p-5 shadow-luxe hover:border-gold/50 transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground">Total Orders</span>
              <div className="h-9 w-9 rounded-full bg-gold/10 grid place-items-center text-gold group-hover:scale-110 transition-transform">
                <Package className="h-4.5 w-4.5" />
              </div>
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-primary">{orders.length}</div>
            <div className="mt-1 text-[0.7rem] text-muted-foreground flex items-center gap-1 font-medium">
              <span>View purchase history</span> <ChevronRight className="h-3 w-3 text-gold" />
            </div>
          </Link>

          {/* Stat 2: Active Deliveries */}
          <Link to="/account/orders" className="bg-card border border-border/80 rounded-[20px] p-5 shadow-luxe hover:border-gold/50 transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground">In Transit</span>
              <div className="h-9 w-9 rounded-full bg-blue-500/10 grid place-items-center text-blue-600 group-hover:scale-110 transition-transform">
                <Truck className="h-4.5 w-4.5" />
              </div>
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-primary">{activeOrdersCount}</div>
            <div className="mt-1 text-[0.7rem] text-muted-foreground flex items-center gap-1 font-medium">
              <span>{activeOrdersCount > 0 ? "Track live shipment" : "No pending shipments"}</span> <ChevronRight className="h-3 w-3 text-gold" />
            </div>
          </Link>

          {/* Stat 3: Wishlist Items */}
          <Link to="/account/wishlist" className="bg-card border border-border/80 rounded-[20px] p-5 shadow-luxe hover:border-gold/50 transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground">Wishlist</span>
              <div className="h-9 w-9 rounded-full bg-rose-500/10 grid place-items-center text-rose-600 group-hover:scale-110 transition-transform">
                <Heart className="h-4.5 w-4.5" />
              </div>
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-primary">{wishlistIds.length}</div>
            <div className="mt-1 text-[0.7rem] text-muted-foreground flex items-center gap-1 font-medium">
              <span>Saved candle rituals</span> <ChevronRight className="h-3 w-3 text-gold" />
            </div>
          </Link>

          {/* Stat 4: Saved Addresses */}
          <Link to="/account/addresses" className="bg-card border border-border/80 rounded-[20px] p-5 shadow-luxe hover:border-gold/50 transition-all group">
            <div className="flex items-center justify-between">
              <span className="text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground">Addresses</span>
              <div className="h-9 w-9 rounded-full bg-emerald-500/10 grid place-items-center text-emerald-600 group-hover:scale-110 transition-transform">
                <MapPin className="h-4.5 w-4.5" />
              </div>
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-primary">{addresses.length}</div>
            <div className="mt-1 text-[0.7rem] text-muted-foreground flex items-center gap-1 font-medium">
              <span>Manage address book</span> <ChevronRight className="h-3 w-3 text-gold" />
            </div>
          </Link>
        </div>

        {/* Recent Order Summary Card */}
        <div className="bg-card border border-border/80 rounded-[24px] p-6 sm:p-8 shadow-luxe space-y-6">
          <div className="flex items-center justify-between border-b border-border/60 pb-4">
            <div>
              <div className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold">Latest Activity</div>
              <h3 className="font-display text-2xl font-bold text-primary mt-0.5">Recent Order</h3>
            </div>
            <Link to="/account/orders" className="text-xs font-semibold text-gold hover:underline flex items-center gap-1">
              View All Orders ({orders.length}) <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {recentOrder ? (
            <div className="bg-background border border-border/70 rounded-[18px] p-5 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4">
                <div>
                  <span className="font-display font-semibold text-lg text-primary block">
                    Order #{recentOrder.id.substring(0, 8).toUpperCase()}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Placed on {new Date(recentOrder.created_at).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>

                <span className="text-[0.65rem] font-semibold uppercase tracking-widest px-3 py-1 rounded-full bg-gold/10 text-gold border border-gold/30">
                  {recentOrder.status}
                </span>
              </div>

              <div className="flex items-center gap-4">
                <img
                  src={recentOrder.items?.[0]?.product?.img || "/placeholder.svg"}
                  alt=""
                  className="h-16 w-16 rounded-[12px] object-cover bg-secondary border border-border shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <h4 className="font-display font-semibold text-base text-primary truncate">
                    {recentOrder.items?.[0]?.product?.name || "Handcrafted Scented Candle"}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {recentOrder.items?.length || 1} item(s) · ₹{recentOrder.totalAmount?.toLocaleString("en-IN")}
                  </p>
                </div>
                <Link to="/account/orders" className="btn-outline-dark h-9 px-4 text-xs font-semibold shrink-0">
                  Track Details
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-background border border-dashed border-border/80 rounded-[18px] space-y-3">
              <Package className="h-10 w-10 text-muted-foreground/50 mx-auto" />
              <div>
                <h4 className="font-display text-lg text-primary">No Orders Placed Yet</h4>
                <p className="text-xs text-muted-foreground mt-1">Explore our candle collections and place your first order.</p>
              </div>
              <Link to="/shop" className="btn-gold inline-flex h-9 px-5 text-xs font-semibold">
                Explore Shop
              </Link>
            </div>
          )}
        </div>

        {/* Default Shipping Address & Quick Actions Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Default Address */}
          <div className="bg-card border border-border/80 rounded-[24px] p-6 shadow-luxe flex flex-col justify-between space-y-4">
            <div>
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold">Primary Location</span>
                <Link to="/account/addresses" className="text-xs text-primary hover:text-gold font-semibold">Manage Addresses</Link>
              </div>
              
              {defaultAddress ? (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-lg text-primary">{defaultAddress.name}</span>
                    <span className="text-[0.55rem] tracking-wider uppercase px-2 py-0.5 rounded-full bg-primary text-cream font-semibold">
                      {defaultAddress.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {defaultAddress.line1}<br />
                    {defaultAddress.city}, {defaultAddress.state} - {defaultAddress.zip}<br />
                    <span className="font-mono text-primary/80">{defaultAddress.phone}</span>
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-xs text-muted-foreground">No default shipping address saved.</p>
              )}
            </div>

            <Link to="/account/addresses" className="text-xs font-semibold text-gold flex items-center gap-1.5 pt-2">
              <MapPin className="h-4 w-4" /> Edit Shipping Addresses ➔
            </Link>
          </div>

          {/* Quick Shortcuts */}
          <div className="bg-card border border-border/80 rounded-[24px] p-6 shadow-luxe space-y-4">
            <div className="border-b border-border/60 pb-3">
              <span className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold">Atelier Shortcuts</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Link to="/shop" className="p-3.5 rounded-[14px] bg-background border border-border/80 hover:border-gold/40 transition-all flex flex-col gap-2 group">
                <ShoppingBag className="h-5 w-5 text-gold group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-display text-sm font-semibold text-primary">Browse Shop</div>
                  <div className="text-[0.68rem] text-muted-foreground">Candles & Gift sets</div>
                </div>
              </Link>

              <Link to="/account/wishlist" className="p-3.5 rounded-[14px] bg-background border border-border/80 hover:border-gold/40 transition-all flex flex-col gap-2 group">
                <Heart className="p-0.5 h-5 w-5 text-rose-500 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-display text-sm font-semibold text-primary">Wishlist</div>
                  <div className="text-[0.68rem] text-muted-foreground">{wishlistIds.length} items saved</div>
                </div>
              </Link>

              <Link to="/account/profile" className="p-3.5 rounded-[14px] bg-background border border-border/80 hover:border-gold/40 transition-all flex flex-col gap-2 group">
                <User className="h-5 w-5 text-gold group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-display text-sm font-semibold text-primary">Profile & Notes</div>
                  <div className="text-[0.68rem] text-muted-foreground">Fragrance palette</div>
                </div>
              </Link>

              <Link to="/contact" className="p-3.5 rounded-[14px] bg-background border border-border/80 hover:border-gold/40 transition-all flex flex-col gap-2 group">
                <Sparkles className="h-5 w-5 text-gold group-hover:scale-110 transition-transform" />
                <div>
                  <div className="font-display text-sm font-semibold text-primary">Concierge Help</div>
                  <div className="text-[0.68rem] text-muted-foreground">Customer support</div>
                </div>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </AccountLayout>
  );
}
